import type { RatePolicy } from '@zyx/shared';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useDeleteRatePolicy, usePutRatePolicy } from '@/hooks/use-indexing';

const CATCH_ALL = 'default';

/** Edits one policy, or creates one when `policy` is absent. The form is mounted only while open, so it seeds from the policy fresh each time. */
export function PolicyDialog(props: {
  readonly policy?: RatePolicy;
  /** Ids already taken, so creating cannot silently replace one. */
  readonly existingIds: ReadonlyArray<string>;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent>{props.open ? <PolicyForm policy={props.policy} existingIds={props.existingIds} onDone={() => props.onOpenChange(false)} /> : null}</DialogContent>
    </Dialog>
  );
}

function PolicyForm(props: { readonly policy?: RatePolicy; readonly existingIds: ReadonlyArray<string>; readonly onDone: () => void }) {
  const existing = props.policy;
  const save = usePutRatePolicy();
  const remove = useDeleteRatePolicy();
  const [policyId, setPolicyId] = useState(existing?.policyId ?? '');
  const [hosts, setHosts] = useState(existing?.hostPatterns.join(', ') ?? '');
  const [requests, setRequests] = useState(String(existing?.requests ?? 1));
  const [windowSeconds, setWindowSeconds] = useState(String((existing?.windowMs ?? 1000) / 1000));
  const [concurrency, setConcurrency] = useState(String(existing?.maxConcurrency ?? 1));
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const taken = existing === undefined && props.existingIds.includes(policyId.trim());
  const catchAll = existing?.policyId === CATCH_ALL;
  const rate = Number(requests) / Number(windowSeconds);

  const submit = () => {
    save.mutate(
      {
        policyId: policyId.trim(),
        hostPatterns: hosts
          .split(',')
          .map((host) => host.trim())
          .filter((host) => host.length > 0),
        requests: Number(requests),
        windowMs: Math.round(Number(windowSeconds) * 1000),
        maxConcurrency: Number(concurrency),
        enabled,
      },
      { onSuccess: props.onDone },
    );
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <DialogHeader>
        <DialogTitle>{existing === undefined ? 'New rate policy' : `Edit ${existing.policyId}`}</DialogTitle>
        <DialogDescription>A change applies to the next request, including those already queued. Requests already queued on a deleted policy finish at its old pace.</DialogDescription>
      </DialogHeader>
      {existing === undefined ? (
        <div className="space-y-1.5">
          <Label htmlFor="policy-id">Name</Label>
          <Input id="policy-id" value={policyId} onChange={(event) => setPolicyId(event.target.value)} placeholder="newsite-pages" autoFocus />
          {taken ? <p className="text-xs text-destructive">A policy with this name exists. Edit it from its row instead.</p> : null}
        </div>
      ) : null}
      <div className="space-y-1.5">
        <Label htmlFor="policy-hosts">Hosts</Label>
        <Input id="policy-hosts" value={hosts} onChange={(event) => setHosts(event.target.value)} placeholder="example.com, *.example-cdn.com" disabled={catchAll} />
        <p className="text-xs text-muted-foreground">Comma-separated. Empty means the policy is used only when a source names it, as for rotating CDN hosts.</p>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="policy-requests">Requests</Label>
          <Input id="policy-requests" type="number" min={1} value={requests} onChange={(event) => setRequests(event.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="policy-window">Per seconds</Label>
          <Input id="policy-window" type="number" min={0.1} step={0.1} value={windowSeconds} onChange={(event) => setWindowSeconds(event.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="policy-concurrency">At once</Label>
          <Input id="policy-concurrency" type="number" min={1} value={concurrency} onChange={(event) => setConcurrency(event.target.value)} />
        </div>
      </div>
      <p className="text-sm text-muted-foreground">{Number.isFinite(rate) && rate > 0 ? `At most ${rate.toFixed(2)} requests per second, ${concurrency} at a time.` : 'Enter a positive rate.'}</p>
      <div className="flex items-center gap-2">
        <Switch id="policy-enabled" checked={enabled} onCheckedChange={setEnabled} disabled={catchAll} />
        <Label htmlFor="policy-enabled" className="font-normal">
          Enabled{catchAll ? ' (the catch-all always is)' : ''}
        </Label>
      </div>
      <DialogFooter className="gap-2 sm:justify-between">
        {existing !== undefined && !catchAll ? (
          <Button
            type="button"
            variant={confirmingDelete ? 'destructive' : 'ghost'}
            className={confirmingDelete ? undefined : 'text-destructive'}
            disabled={remove.isPending}
            // Two clicks: the first asks, the second deletes.
            onClick={() => (confirmingDelete ? remove.mutate(existing.policyId, { onSuccess: props.onDone }) : setConfirmingDelete(true))}
          >
            {confirmingDelete ? `Delete ${existing.policyId}` : 'Delete'}
          </Button>
        ) : (
          <span />
        )}
        <Button type="submit" disabled={save.isPending || taken}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </form>
  );
}
